import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeIcon,
  PracticeTestIcon,
  DocumentIcon,
  TopicFocusIcon,
  UserIcon,
} from "components/atoms/Icon";
import { NavItems } from "components/molecules/NavItems";
import { useGetSettingsURLQuery } from "services/myAccount";
import { logOut } from "../../../state/reducers/AuthSlice";
import { setIsShowACTPopup, setToggleMobileMenu, closeIframeModal } from "../../../state/reducers/layoutSlice";
import { PURGE } from "redux-persist";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { GMAT_TEST_TYPE, LSAT_TEST_TYPE } from "utils/constants/TestTypes";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
  isMobile: boolean;
  isTab: boolean;
  isDesktop: boolean;
  payload?: any;
}

export const SelfPacedNavBar = ({ isMobile, payload }: IProps) => {
  const [activeTab, setActiveTab] = useState(0);
  // const [toggleMobileMenu, setToggleMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isError } = useGetSettingsURLQuery();

  const [tabs, setTabs] = useState<any>([]);

  const dispatch = useAppDispatch();

  const { toggleMobileMenu } = useAppSelector(
    (state: {
      layout: {
        toggleMobileMenu: boolean;
      };
    }) => state.layout
  );

  useEffect(() => {
    setTabs([
      ...(payload &&
      payload.hasOwnProperty("showhomepage") &&
      payload.showhomepage.toLowerCase() === "true"
        ? [
            {
              id: 0,
              icon: <HomeIcon color={activeTab === 0 ? "#273E5A" : "#eee"} />,
              label:
                payload &&
                payload.hasOwnProperty("Home") &&
                payload.hasOwnProperty("Home") !== ""
                  ? payload.Home
                  : "Home",
              route: "",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showpracticepage") &&
      payload.showpracticepage.toLowerCase() === "true"
        ? [
            {
              id: 1,
              icon: (
                <PracticeTestIcon
                  color={activeTab === 1 ? "#273E5A" : "#eee"}
                />
              ),
              label:
                payload &&
                payload.hasOwnProperty("Practice") &&
                payload.hasOwnProperty("Practice") !== ""
                  ? payload.Practice
                  : payload.testtype === GMAT_TEST_TYPE ||
                    payload.testtype === LSAT_TEST_TYPE
                  ? "Practice"
                  : "Test",
              route: "test",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showcourseindex") &&
      payload.showcourseindex.toLowerCase() === "true"
        ? [
            {
              id: 2,
              icon: (
                <DocumentIcon color={activeTab === 2 ? "#273E5A" : "#eee"} />
              ),
              label:
                payload &&
                payload.hasOwnProperty("CourseIndex") &&
                payload.hasOwnProperty("CourseIndex") !== ""
                  ? payload.CourseIndex
                  : "Course Index",
              route: "course-index",
            },
          ]
        : []),
      ...(payload &&
      payload.hasOwnProperty("showsupplementalsessions") &&
      payload.showsupplementalsessions.toLowerCase() === "true"
        ? [
            {
              id: 3,
              icon: (
                <TopicFocusIcon color={activeTab === 3 ? "#273E5A" : "#eee"} />
              ),
              label:
                payload &&
                payload.hasOwnProperty("SupplementalSessions") &&
                payload.hasOwnProperty("SupplementalSessions") !== ""
                  ? payload.SupplementalSessions
                  : `${getOfficeHoursDisplayName(payload?.testtype || "")} Sessions`,
              route: "topic-focus-sessions",
            },
          ]
        : []),
      ...(data && !isError
        ? [
            {
              id: 4,
              icon: <UserIcon color={activeTab === 4 ? "#273E5A" : "#eee"} />,
              label: "My Account",
              route: "my-account",
              dropdown: {
                isVisible: false,
                routes: [
                  { id: 1, label: "Account Details" },
                  { id: 2, label: "My Courses" },
                  { id: 3, label: "Log Out" },
                ],
              },
            },
          ]
        : []),
    ]);
  }, [payload, activeTab, data, isError]);

  const handleLogout = () => {
    dispatch(logOut());
    dispatch({ type: PURGE, key: "auth", result: () => null });
    localStorage.clear();
    dispatch(setIsShowACTPopup(false));
    window.location.href = `${process.env.REACT_APP_RETURN_URL}`;
  };

  const handleNavigation = (
    tabId: number,
    route: string,
    subRouteId?: number
  ) => {
    dispatch(closeIframeModal());
    if (route === "my-account") {
      if (subRouteId) {
        dispatch(setToggleMobileMenu(false));
        switch (subRouteId) {
          case 1:
            window.location.replace(data);
            break;
          case 2:
            process.env.REACT_APP_HOSTED_URL &&
              window.location.replace(process.env.REACT_APP_HOSTED_URL);
            break;
          case 3:
            handleLogout();
        }
      } else {
        setTabs(
          tabs.map((tab: any) => {
            if (tab.id === 4) {
              const updatedTab = { ...tab };
              updatedTab.dropdown.isVisible = !tab.dropdown.isVisible;
              return updatedTab;
            }
            return tab;
          })
        );
      }
    } else {
      dispatch(setToggleMobileMenu(false));
      setActiveTab(tabId);
      navigate(route);
    }
  };

  useEffect(() => {
    switch (location.pathname) {
      case "/self-paced":
        setActiveTab(0);
        break;
      case "/self-paced/test":
        setActiveTab(1);
        break;
      case "/self-paced/course-index":
        setActiveTab(2);
        break;
      case "/self-paced/topic-focus-sessions":
        setActiveTab(3);
        break;
      default:
        break;
    }
  }, [location.pathname]);

  return (
    <NavItems
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      toggleItemsView={isMobile && toggleMobileMenu}
      handleNavigation={handleNavigation}
    />
  );
};
